import { VENDOR_TRADES, VENDOR_TRADE_LABELS, type VendorTrade } from '@sitewise/shared';
import { Button, Input, Label, toast } from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { fetchVendorEmailInbox } from '../../../api/ai.js';
import { createVendorCompany, fetchVendorCompanies } from '../../../api/vendor-companies.js';

export function VendorsSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['vendor-companies'],
    queryFn: fetchVendorCompanies,
  });
  const inboxQuery = useQuery({
    queryKey: ['vendor-email-inbox'],
    queryFn: fetchVendorEmailInbox,
  });
  const [name, setName] = useState('');
  const [trade, setTrade] = useState<VendorTrade>('other');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createVendorCompany({
        name: name.trim(),
        trade,
        primaryContactEmail: email.trim() || null,
      }),
    onSuccess: async () => {
      setName('');
      setEmail('');
      setError(null);
      await qc.invalidateQueries({ queryKey: ['vendor-companies'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-fg">Vendors</h1>
        <p className="text-sm text-muted">External vendor companies and technicians.</p>
      </div>

      {inboxQuery.data?.inbox ? (
        <div className="max-w-2xl rounded-lg border border-border bg-surface-muted/40 p-4 text-sm">
          <p className="font-medium text-fg">Vendor reply inbox</p>
          <p className="mt-1 text-fg-secondary">
            Vendors can reply to assignment emails or forward updates to this address. Matched
            replies land on the work order; uncertain matches go to{' '}
            <Link to="/ai/review-queue" className="text-primary hover:underline">
              AI inbox
            </Link>
            .
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="rounded border border-border bg-surface px-2 py-1 text-xs">
              {inboxQuery.data.inbox.address}
            </code>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(inboxQuery.data.inbox.address);
                  toast.success('Copied inbox address');
                } catch {
                  toast.error('Could not copy');
                }
              }}
            >
              Copy
            </Button>
            {!inboxQuery.data.inbox.isActive ? (
              <span className="text-xs text-warning">Inbox inactive</span>
            ) : null}
          </div>
        </div>
      ) : null}

      <form
        className="grid max-w-2xl gap-3 rounded-lg border border-border p-4 md:grid-cols-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!name.trim()) return;
          createMutation.mutate();
        }}
      >
        <div className="space-y-1 md:col-span-1">
          <Label htmlFor="vendor-name">Company name</Label>
          <Input id="vendor-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="space-y-1 md:col-span-1">
          <Label htmlFor="vendor-trade">Trade</Label>
          <select
            id="vendor-trade"
            value={trade}
            onChange={(e) => setTrade(e.target.value as VendorTrade)}
            className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
          >
            {VENDOR_TRADES.map((t) => (
              <option key={t} value={t}>
                {VENDOR_TRADE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1 md:col-span-1">
          <Label htmlFor="vendor-email">Contact email (optional)</Label>
          <Input
            id="vendor-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="md:col-span-3">
          <Button type="submit" disabled={createMutation.isPending}>
            Add vendor company
          </Button>
          {error ? <span className="ml-3 text-sm text-danger">{error}</span> : null}
        </div>
      </form>

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (data?.vendorCompanies ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">No vendor companies yet.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {(data?.vendorCompanies ?? []).map((c) => (
            <li key={c.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-medium text-fg">{c.name}</p>
                <p className="text-xs text-fg-muted">{VENDOR_TRADE_LABELS[c.trade]}</p>
              </div>
              <Link to={`/settings/vendors/${c.id}`} className="text-sm text-primary hover:underline">
                Manage
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
